// FormFieldsFF.tsx
import React, { useState } from "react";
import type { FormikProps } from "formik";
import { getIn } from "formik";
import Select from "react-select";

/** Common types */
export type FFOption = { value: string; label: string };

/* =========================
 * 1) UIInputFF
 * ========================= */
export function UIInputFF({
  formik,
  name,
  label,
  required,
  placeholder,
  type = "text",
  asTextarea = false,
  rows = 4,
  hideZero = false, // ✅ NEW
}: {
  formik: FormikProps<any>;
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  type?: string;
  asTextarea?: boolean;
  rows?: number;
  hideZero?: boolean; // ✅ NEW
}) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const touched = getIn(formik.touched, name);
  const error = getIn(formik.errors, name) as string | undefined;
  const hasError = !!(touched && error);

  const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";
  const borderColor = hasError ? "#747981" : hover || focus ? "#9ca3af" : "#c9ced6";

  const baseStyle: React.CSSProperties = {
    width: "100%",
    height: asTextarea ? undefined : 39,
    borderRadius: 10,
    padding: asTextarea ? "10px 12px" : "10px 12px",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor,
    backgroundColor: bgColor,
    outline: "none",
    transition: "all .2s ease-in-out",
    boxShadow: "none",
    color: "#111827",
    resize: asTextarea ? "vertical" : undefined,
  };

  const rawValue = getIn(formik.values, name) ?? "";
  const isEmailField = /email/i.test(String(name || ""));
  const emailAutoComplete = isEmailField ? "nope" : undefined;

  // ✅ show empty when value is 0 (only if hideZero + number input)
  const valueToShow =
    !asTextarea && hideZero && type === "number" && (rawValue === 0 || rawValue === "0")
      ? ""
      : rawValue;

  return (
    <div className="mb-3">
      {label !== undefined && (
        <label className="form-label fs-13">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      {asTextarea ? (
        <textarea
          id={name}
          name={name}
          className={`form-control ${hasError ? "is-invalid" : ""}`}
          style={baseStyle}
          rows={rows}
          value={rawValue}
          onChange={(e) => formik.setFieldValue(name, e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            formik.setFieldTouched(name, true);
            formik.handleBlur(e);
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          placeholder={placeholder}
          aria-invalid={hasError}
          autoComplete={emailAutoComplete}
          spellCheck={isEmailField ? false : undefined}
        />
      ) : (
        <input
          id={name}
          name={name}
          className={`form-control ${hasError ? "is-invalid" : ""}`}
          style={baseStyle}
          value={valueToShow}
          onChange={(e) => {
            const raw = e.target.value;

            // ✅ if number + hideZero: store 0 when cleared, else store number
            if (hideZero && type === "number") {
              const next = raw === "" ? 0 : Number(raw);
              formik.setFieldValue(name, Number.isFinite(next) ? next : 0);
              return;
            }

            // default behavior
            formik.setFieldValue(name, raw);
          }}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            formik.setFieldTouched(name, true);
            formik.handleBlur(e);
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          placeholder={placeholder}
          type={type}
          aria-invalid={hasError}
          autoComplete={emailAutoComplete}
          spellCheck={isEmailField ? false : undefined}
          data-lpignore={isEmailField ? "true" : undefined}
          data-1p-ignore={isEmailField ? "true" : undefined}
          data-bwignore={isEmailField ? "true" : undefined}
          onKeyDown={(e) => {
            // ✅ prevent negative/e/+ for number fields
            if (type === "number" && (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+")) {
              e.preventDefault();
            }
          }}
        />
      )}

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}

// export function UIInputFF({
//   formik,
//   name,
//   label,
//   required,
//   placeholder,
//   type = "text",
//   asTextarea = false,
//   rows = 4,
// }: {
//   formik: FormikProps<any>;
//   name: string;
//   label?: string;
//   required?: boolean;
//   placeholder?: string;
//   type?: string;
//   asTextarea?: boolean;
//   rows?: number;
// }) {
//   const [hover, setHover] = useState(false);
//   const [focus, setFocus] = useState(false);

//   const touched = getIn(formik.touched, name);
//   const error = getIn(formik.errors, name) as string | undefined;
//   const hasError = !!(touched && error);

//   const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";
//   const borderColor = hasError ? "#747981" : hover || focus ? "#9ca3af" : "#c9ced6";

//   const baseStyle: React.CSSProperties = {
//     width: "100%",
//     height: asTextarea ? undefined : 39,
//     borderRadius: 10,
//     padding: asTextarea ? "10px 12px" : "10px 12px",
//     borderWidth: 2,
//     borderStyle: "solid",
//     borderColor,
//     backgroundColor: bgColor,
//     outline: "none",
//     transition: "all .2s ease-in-out",
//     boxShadow: "none",
//     color: "#111827",
//     resize: asTextarea ? "vertical" : undefined,
//   };

//   const value = getIn(formik.values, name) ?? "";

//   return (
//     <div className="mb-3">
//       {label !== undefined && (
//         <label className="form-label fs-13">
//           {label} {required && <span className="text-danger">*</span>}
//         </label>
//       )}

//       {asTextarea ? (
//         <textarea
//           id={name}
//           name={name}
//           className={`form-control ${hasError ? "is-invalid" : ""}`}
//           style={baseStyle}
//           rows={rows}
//           value={value}
//           onChange={(e) => formik.setFieldValue(name, e.target.value)}
//           onFocus={() => setFocus(true)}
//           onBlur={(e) => {
//             setFocus(false);
//             formik.setFieldTouched(name, true);
//             formik.handleBlur(e);
//           }}
//           onMouseEnter={() => setHover(true)}
//           onMouseLeave={() => setHover(false)}
//           placeholder={placeholder}
//           aria-invalid={hasError}
//         />
//       ) : (
//         <input
//           id={name}
//           name={name}
//           className={`form-control ${hasError ? "is-invalid" : ""}`}
//           style={baseStyle}
//           value={value}
//           onChange={(e) => formik.setFieldValue(name, e.target.value)}
//           onFocus={() => setFocus(true)}
//           onBlur={(e) => {
//             setFocus(false);
//             formik.setFieldTouched(name, true);
//             formik.handleBlur(e);
//           }}
//           onMouseEnter={() => setHover(true)}
//           onMouseLeave={() => setHover(false)}
//           placeholder={placeholder}
//           type={type}
//           aria-invalid={hasError}
//         />
//       )}

//       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//     </div>
//   );
// }

/* =========================
 * 2) UIReactSelectFF
 * ========================= */
export function UIReactSelectFF({
  formik,
  name,
  label,
  required,
  placeholder = "Select...",
  options,
  isClearable = false,
  isDisabled = false,
}: {
  formik: FormikProps<any>;
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  options: FFOption[];
  isClearable?: boolean;
  isDisabled?: boolean;
}) {
  const touched = getIn(formik.touched, name);
  const error = getIn(formik.errors, name) as string | undefined;
  const hasError = !!(touched && error);

  const currentValue = options.find((o) => o.value === getIn(formik.values, name)) || null;

  // Styles to visually match UIInputFF
  const styles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 39,
      height: 39,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: hasError ? "#747981" : state.isFocused ? "#9ca3af" : "#c9ced6",
      backgroundColor: state.isFocused ? "#fafafa" : "#f5f6fa",
      boxShadow: "none",
      "&:hover": { borderColor: "#9ca3af" },
    }),
    valueContainer: (base: any) => ({ ...base, padding: "2px 10px" }),
    indicatorsContainer: (base: any) => ({ ...base, height: 35 }),
    dropdownIndicator: (base: any) => ({ ...base, paddingRight: 10 }),
    clearIndicator: (base: any) => ({ ...base, paddingRight: 6 }),
    menu: (base: any) => ({ ...base, zIndex: 5 }),
  };

  return (
    <div className="mb-3">
      {label !== undefined && (
        <label className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <Select
        inputId={name}
        name={name}
        options={options}
        value={currentValue}
        onChange={(opt) => formik.setFieldValue(name, (opt as any)?.value ?? "")}
        onBlur={() => formik.setFieldTouched(name, true)}
        placeholder={placeholder}
        classNamePrefix="rs"
        styles={styles}
        isDisabled={isDisabled}
        isClearable={isClearable}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}
